import { ReactNode, useRef } from "react";

import {
  Box,
  MenuItem,
  Select,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridPagination,
  GridRowClassNameParams,
  GridSortModel,
  useGridApiContext,
  useGridSelector,
  gridPaginationModelSelector,
  gridRowCountSelector,
} from "@mui/x-data-grid";

import { isSmallScreen } from "@/common/utils";
import SearchBar from "@/components/alerts/Search/SearchBar";

import { SubmissionSort } from "./submissionSort";

export interface PaginationModel {
  page: number;
  pageSize: number;
}

type SearchView = "card" | "list" | "map";

// The MIT DataGrid throws above this; card view can grow the shared model past it.
const MAX_GRID_PAGE_SIZE = 100;

export interface SubmissionListViewProps<T extends { id: string }> {
  rows: T[];
  columns: GridColDef[];
  rowCount: number;
  loading: boolean;

  paginationModel: PaginationModel;
  onPaginationModelChange: (model: PaginationModel) => void;

  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  /** SearchBar copy variant; defaults to "report". */
  searchType?: "alert" | "report" | "registry";

  /** Toolbar sort control; pair with `sortModel` so header arrows reflect the same sort. */
  sort?: SubmissionSort;
  onSortChange?: (sort: SubmissionSort) => void;

  /** Server-side per-column sort. When `onSortModelChange` is provided the grid switches to
   * server sorting and surfaces sort arrows on `sortable` columns. */
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;

  /** Omit to hide the filters affordance entirely (e.g. the partner list). */
  filtersVisible?: boolean;
  setFiltersVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  filterChips?: ReactNode;
  /** Rendered below the search bar; receives the search bar as its popover anchor. */
  renderFilterPanel?: (anchorEl: HTMLElement | null) => ReactNode;
  /** Rendered above the search bar, outside the filters section — for quick toggles/actions. */
  toolbarActions?: ReactNode;

  /** Enabled views. Fewer than two → the toggle is hidden and the grid always renders. */
  activeViews?: SearchView[];
  searchView?: SearchView;
  setSearchView?: (view: SearchView) => void;
  renderCard?: () => ReactNode;
  renderMap?: () => ReactNode;

  onRowClick?: (row: T) => void;
  getRowClassName?: (params: GridRowClassNameParams<T>) => string;
  /** Extra styles merged onto the grid — e.g. to style rows by a `getRowClassName` class. */
  sx?: SxProps<Theme>;

  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: (model: Record<string, boolean>) => void;
}

function CustomPagination() {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const pageCount = Math.max(
    1,
    Math.ceil(rowCount / (paginationModel.pageSize || 1)),
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
      <Typography variant="body2" sx={{ mr: 1 }}>
        Page:
      </Typography>
      <Select
        size="small"
        value={paginationModel.page}
        onChange={(e) => apiRef.current.setPage(Number(e.target.value))}
        sx={{ minWidth: 60 }}
      >
        {Array.from({ length: pageCount }, (_, i) => (
          <MenuItem key={i} value={i}>
            {i + 1}
          </MenuItem>
        ))}
      </Select>
      <GridPagination />
    </Box>
  );
}

/**
 * Presentational, column-configurable submission list: search bar + server-paginated
 * DataGrid, with optional card/map views and a filter panel. Data fetching and any detail
 * drawer live in the caller — this owns only the list surface so the police report list and
 * the partner list can share it.
 */
function SubmissionListView<T extends { id: string }>({
  rows,
  columns,
  rowCount,
  loading,
  paginationModel,
  onPaginationModelChange,
  searchText,
  setSearchText,
  searchType = "report",
  sort,
  onSortChange,
  sortModel,
  onSortModelChange,
  filtersVisible = false,
  setFiltersVisible,
  filterChips,
  renderFilterPanel,
  toolbarActions,
  activeViews = [],
  searchView = "list",
  setSearchView,
  renderCard,
  renderMap,
  onRowClick,
  getRowClassName,
  sx,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: SubmissionListViewProps<T>) {
  const searchBarRef = useRef<HTMLInputElement | null>(null);
  const mobileView = isSmallScreen();

  const mobileViewStyle = {
    height: "calc(100vh - 130px)",
    paddingTop: "16px",
    width: "95vw",
  };

  const desktopStyle = {
    height: "calc(100vh - 180px)",
    width: "calc(100% - 32px)",
    marginLeft: "16px",
    marginRight: "16px",
  };

  const gridPaginationModel = {
    ...paginationModel,
    pageSize: Math.min(paginationModel.pageSize, MAX_GRID_PAGE_SIZE),
  };

  const renderListView = () => (
    <DataGrid
      rows={rows}
      columns={columns}
      autoPageSize
      rowSelection={false}
      slots={{ pagination: CustomPagination }}
      sx={[
        {
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: "inherit",
              display: "flex",
              alignItems: "center",
            },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      onPaginationModelChange={onPaginationModelChange}
      initialState={{ pagination: { paginationModel: gridPaginationModel } }}
      rowCount={rowCount}
      loading={loading}
      paginationMode="server"
      paginationModel={gridPaginationModel}
      sortingMode={onSortModelChange ? "server" : undefined}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      onRowClick={(params) => onRowClick?.(params.row as T)}
      getRowClassName={getRowClassName}
    />
  );

  return (
    <div style={{ position: "relative" }}>
      <div style={mobileView ? mobileViewStyle : desktopStyle}>
        <div className="search-bar-container" style={{ marginBottom: "16px" }}>
          <SearchBar
            getMoreItems={() => {}}
            onSearch={() => {}}
            onClearSearch={() => {}}
            searchView={searchView}
            setSearchView={setSearchView ?? (() => {})}
            defaultView="list"
            searchText={searchText}
            setSearchText={setSearchText}
            currentItems={rows}
            setCurrentItems={() => {}}
            filtersVisible={filtersVisible}
            setFiltersVisible={setFiltersVisible ?? (() => {})}
            showFilters={setFiltersVisible !== undefined}
            totalAlerts={rowCount}
            loading={loading}
            setLoading={() => {}}
            searchType={searchType}
            filtersRef={searchBarRef}
            filterChips={filterChips}
            activeViews={activeViews}
            sort={sort}
            onSortChange={onSortChange}
            toolbarActions={toolbarActions}
          />
          {filtersVisible && renderFilterPanel?.(searchBarRef.current)}
        </div>
        {searchView === "card" && renderCard
          ? renderCard()
          : searchView === "map" && renderMap
            ? renderMap()
            : renderListView()}
      </div>
    </div>
  );
}

export default SubmissionListView;
